/**
 * Audio Analyzer - Extract frequency data for all frames using OfflineAudioContext
 */

import { AudioFeatureExtractor } from './lib/audio-features.js';

class AudioAnalyzer {
  constructor(options = {}) {
    this.fftSize = options.fftSize || 2048;
    this.fps = options.fps || 30;
    this.onProgress = options.onProgress || null;
  }

  // Backward compatibility guard for environments that might load an older cached version
  createFrequencyStreamPlaceholder() {
    throw new Error('createFrequencyStream not implemented in this AudioAnalyzer build');
  }

  /**
   * Create a streaming frequency data generator for a playlist.
   * Processes one track at a time, yielding per-frame frequency arrays without
   * holding the entire playlist in memory. Optionally calls onTrackAudio to
   * allow streaming audio encoding per track.
   *
   * Each yielded frame includes enhanced audio features (bands, spectral, rhythm)
   * computed lazily - only accessed features incur computation cost.
   *
   * @param {Array} items - playlist items ({file, audioBuffer?, duration, included})
   * @param {Object} options
   * @param {number} options.fftSize
   * @param {number} options.fps
   * @param {number} options.overlapSamples - samples to carry over between tracks (for crossfade continuity)
   * @param {(buffer: AudioBuffer, timeOffsetSec: number, trackIndex: number)=>Promise<void>} options.onTrackAudio
   * @returns {{frames: AsyncGenerator<{data:Uint8Array, features:AudioFeatures, trackIndex:number, frameTime:number}>, ready: Promise<{sampleRate:number, numberOfChannels:number}>, estimatedFrameCount:number}}
   */
  createFrequencyStream(items, options = {}) {
    const fftSize = options.fftSize || this.fftSize;
    const fps = options.fps || this.fps;
    const overlapSamples = options.overlapSamples || fftSize; // one window by default
    const onTrackAudio = options.onTrackAudio;
    const firstBuffer = options.firstBuffer || null;

    // Estimate frames from known durations if available
    const estimatedFrameCount = items.reduce((sum, item) => {
      return sum + (item.duration ? Math.ceil(item.duration * fps) : 0);
    }, 0);

    let resolveReady;
    let readyResolved = false;
    const ready = new Promise(res => { resolveReady = res; });

    const resolveReadyOnce = (info) => {
      if (readyResolved) return;
      readyResolved = true;
      resolveReady(info);
    };

    // If caller provided firstBuffer, we can resolve ready immediately
    if (firstBuffer) {
      resolveReadyOnce({
        sampleRate: firstBuffer.sampleRate,
        numberOfChannels: firstBuffer.numberOfChannels
      });
    }

    // Create feature extractor for this export session
    // Will be initialized with first buffer's sample rate
    let featureExtractor = null;

    const frames = (async function* streamFrames() {
      let prevTail = new Float32Array(0);
      let trackStartTime = 0;

      for (let trackIndex = 0; trackIndex < items.length; trackIndex++) {
        const item = items[trackIndex];
        if (!item.included) continue;

        let audioBuffer;
        if (firstBuffer && trackIndex === 0) {
          audioBuffer = firstBuffer;
        } else {
          const audioSource = item.rawAudioData || item.file;
          if (!item.audioBuffer && !audioSource) {
            throw new Error(`Track "${item.name || 'unknown'}" has no audio source`);
          }
          audioBuffer = item.audioBuffer || await this.loadAudio(audioSource);
        }
        if (!item.duration) {
          item.duration = audioBuffer.duration;
        }

        if (trackIndex === 0 && !readyResolved) {
          resolveReadyOnce({
            sampleRate: audioBuffer.sampleRate,
            numberOfChannels: audioBuffer.numberOfChannels
          });
        }

        // Initialize feature extractor on first buffer (need sample rate)
        if (!featureExtractor) {
          featureExtractor = new AudioFeatureExtractor({
            sampleRate: audioBuffer.sampleRate,
            fftSize
          });
        } else {
          // Reset between tracks to clear onset history
          featureExtractor.reset();
        }

        // Allow caller to encode audio for this track before we release it
        if (onTrackAudio) {
          await onTrackAudio(audioBuffer, trackStartTime, trackIndex);
        }

        const monoData = this.mixToMono(audioBuffer);
        const samplesPerFrame = Math.max(1, Math.floor(audioBuffer.sampleRate / fps));

        // Merge tail from previous track to preserve continuity across joins
        const merged = this.mergeWithTail(prevTail, monoData);

        const frequencyBinCount = fftSize / 2;
        const totalSamples = merged.length;
        const frameCountTrack = Math.max(1, Math.ceil(totalSamples / samplesPerFrame));

        for (let frame = 0; frame < frameCountTrack; frame++) {
          const startSample = frame * samplesPerFrame;
          if (startSample >= totalSamples) break;

          const samples = new Float32Array(fftSize);
          for (let i = 0; i < fftSize && startSample + i < totalSamples; i++) {
            samples[i] = merged[startSample + i];
          }

          // Apply Hanning window
          for (let i = 0; i < fftSize; i++) {
            samples[i] *= 0.5 * (1 - Math.cos((2 * Math.PI * i) / (fftSize - 1)));
          }

          // FFT
          const fftResult = this.performFFT(samples);

          // Convert to byte frequency data (0-255)
          const byteData = new Uint8Array(frequencyBinCount);
          for (let i = 0; i < frequencyBinCount; i++) {
            const magnitude = fftResult[i];
            const db = 20 * Math.log10(magnitude + 1e-10);
            const normalized = Math.max(0, Math.min(255, ((db + 100) / 100) * 255));
            byteData[i] = Math.round(normalized);
          }

          const frameTime = trackStartTime + (startSample / audioBuffer.sampleRate);

          // Extract enhanced audio features for this frame
          const features = featureExtractor.extract(byteData, samples);

          yield {
            data: byteData,
            features,
            trackIndex,
            frameTime
          };

          if (this.onProgress && estimatedFrameCount > 0) {
            const approxFrameIndex = Math.min(estimatedFrameCount, Math.round(frameTime * fps));
            this.onProgress(approxFrameIndex / estimatedFrameCount);
          }
        }

        // Carry overlap tail into next track
        const tailStart = Math.max(0, merged.length - overlapSamples);
        prevTail = merged.subarray(tailStart);
        trackStartTime += audioBuffer.duration;
      }
    }).call(this);

    return {
      frames,
      ready,
      estimatedFrameCount
    };
  }

  /**
   * Merge previous tail samples with current track data.
   */
  mergeWithTail(tail, data) {
    if (!tail || tail.length === 0) return data;
    const merged = new Float32Array(tail.length + data.length);
    merged.set(tail, 0);
    merged.set(data, tail.length);
    return merged;
  }

  /**
   * Mix an AudioBuffer to mono Float32Array
   */
  mixToMono(audioBuffer) {
    if (audioBuffer.numberOfChannels === 1) {
      // Return a copy to avoid mutating the original buffer
      return new Float32Array(audioBuffer.getChannelData(0));
    }
    const left = audioBuffer.getChannelData(0);
    const right = audioBuffer.getChannelData(1);
    const mono = new Float32Array(left.length);
    for (let i = 0; i < left.length; i++) {
      mono[i] = (left[i] + right[i]) / 2;
    }
    return mono;
  }

  /**
   * Load and decode audio file
   * @param {File|Blob|ArrayBuffer} audioSource
   * @returns {Promise<AudioBuffer>}
   */
  async loadAudio(audioSource) {
    let arrayBuffer;

    if (audioSource instanceof ArrayBuffer) {
      arrayBuffer = audioSource;
    } else if (audioSource instanceof Blob || audioSource instanceof File) {
      arrayBuffer = await audioSource.arrayBuffer();
    } else {
      throw new Error('Invalid audio source');
    }

    const audioContext = new AudioContext();
    try {
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      return audioBuffer;
    } finally {
      await audioContext.close();
    }
  }

  /**
   * Extract frequency data using manual FFT on audio samples
   * @param {AudioBuffer} audioBuffer
   * @returns {Promise<{frequencyData: Uint8Array[], duration: number, frameCount: number}>}
   */
  async extractFrequencyDataDirect(audioBuffer) {
    const { fftSize, fps } = this;
    const sampleRate = audioBuffer.sampleRate;
    const duration = audioBuffer.duration;
    const frameCount = Math.ceil(duration * fps);
    const samplesPerFrame = Math.floor(sampleRate / fps);

    // Get audio data (mix to mono if stereo)
    let audioData;
    if (audioBuffer.numberOfChannels === 1) {
      audioData = audioBuffer.getChannelData(0);
    } else {
      // Mix to mono with yielding for long files
      const left = audioBuffer.getChannelData(0);
      const right = audioBuffer.getChannelData(1);
      audioData = new Float32Array(left.length);
      for (let i = 0; i < left.length; i++) {
        audioData[i] = (left[i] + right[i]) / 2;
        // Yield every 100k samples
        if (i % 100000 === 0 && i > 0) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
    }

    const frequencyData = [];
    const frequencyBinCount = fftSize / 2;

    for (let frame = 0; frame < frameCount; frame++) {
      const startSample = frame * samplesPerFrame;
      const endSample = Math.min(startSample + fftSize, audioData.length);

      // Extract samples for this frame
      const samples = new Float32Array(fftSize);
      for (let i = 0; i < fftSize && startSample + i < audioData.length; i++) {
        samples[i] = audioData[startSample + i];
      }

      // Apply Hanning window
      for (let i = 0; i < fftSize; i++) {
        samples[i] *= 0.5 * (1 - Math.cos((2 * Math.PI * i) / (fftSize - 1)));
      }

      // Perform FFT
      const fftResult = this.performFFT(samples);

      // Convert to byte frequency data (0-255)
      const byteData = new Uint8Array(frequencyBinCount);
      for (let i = 0; i < frequencyBinCount; i++) {
        // Calculate magnitude in dB, scale to 0-255
        const magnitude = fftResult[i];
        const db = 20 * Math.log10(magnitude + 1e-10);
        // Map -100dB to 0dB range to 0-255
        const normalized = Math.max(0, Math.min(255, ((db + 100) / 100) * 255));
        byteData[i] = Math.round(normalized);
      }

      frequencyData.push(byteData);

      if (this.onProgress) {
        this.onProgress((frame + 1) / frameCount);
      }

      // Yield every 3 frames to prevent UI freeze (DFT is slow)
      if (frame % 3 === 0) {
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    }

    return {
      frequencyData,
      duration,
      frameCount,
      sampleRate,
      fps
    };
  }

  /**
   * Cooley-Tukey FFT implementation - O(n log n) instead of O(n²)
   * @param {Float32Array} samples
   * @returns {Float32Array} Magnitude spectrum
   */
  performFFT(samples) {
    const n = samples.length;

    // Create complex arrays (real and imaginary parts)
    const real = new Float32Array(n);
    const imag = new Float32Array(n);

    // Copy samples to real part
    for (let i = 0; i < n; i++) {
      real[i] = samples[i];
      imag[i] = 0;
    }

    // Bit-reversal permutation
    const bits = Math.log2(n);
    for (let i = 0; i < n; i++) {
      const j = this.reverseBits(i, bits);
      if (j > i) {
        [real[i], real[j]] = [real[j], real[i]];
        [imag[i], imag[j]] = [imag[j], imag[i]];
      }
    }

    // Cooley-Tukey iterative FFT
    for (let size = 2; size <= n; size *= 2) {
      const halfSize = size / 2;
      const angleStep = -2 * Math.PI / size;

      for (let i = 0; i < n; i += size) {
        for (let j = 0; j < halfSize; j++) {
          const angle = angleStep * j;
          const cosA = Math.cos(angle);
          const sinA = Math.sin(angle);

          const k = i + j;
          const l = k + halfSize;

          const tReal = real[l] * cosA - imag[l] * sinA;
          const tImag = real[l] * sinA + imag[l] * cosA;

          real[l] = real[k] - tReal;
          imag[l] = imag[k] - tImag;
          real[k] = real[k] + tReal;
          imag[k] = imag[k] + tImag;
        }
      }
    }

    // Calculate magnitudes for first half (positive frequencies)
    const magnitudes = new Float32Array(n / 2);
    for (let i = 0; i < n / 2; i++) {
      magnitudes[i] = Math.sqrt(real[i] * real[i] + imag[i] * imag[i]) / n;
    }

    return magnitudes;
  }

  /**
   * Reverse bits of an integer
   */
  reverseBits(x, bits) {
    let result = 0;
    for (let i = 0; i < bits; i++) {
      result = (result << 1) | (x & 1);
      x >>= 1;
    }
    return result;
  }

  /**
   * Concatenate multiple audio buffers into a single buffer
   * @param {AudioBuffer[]} buffers - Array of audio buffers to concatenate
   * @returns {AudioBuffer} - Combined audio buffer
   */
  concatenateAudioBuffers(buffers) {
    if (buffers.length === 0) {
      throw new Error('No audio buffers to concatenate');
    }

    if (buffers.length === 1) {
      return buffers[0];
    }

    // Use first buffer's properties as reference
    const sampleRate = buffers[0].sampleRate;
    const numChannels = buffers[0].numberOfChannels;

    // Calculate total length
    const totalLength = buffers.reduce((sum, buf) => sum + buf.length, 0);

    // Create offline context to create the output buffer
    const offlineCtx = new OfflineAudioContext(numChannels, totalLength, sampleRate);
    const outputBuffer = offlineCtx.createBuffer(numChannels, totalLength, sampleRate);

    // Copy each buffer's samples
    let offset = 0;
    for (const buffer of buffers) {
      for (let channel = 0; channel < numChannels; channel++) {
        const outputChannelData = outputBuffer.getChannelData(channel);
        // Handle mismatched channel counts
        if (channel < buffer.numberOfChannels) {
          const inputChannelData = buffer.getChannelData(channel);
          outputChannelData.set(inputChannelData, offset);
        } else {
          // If input has fewer channels, copy the last available channel
          const inputChannelData = buffer.getChannelData(buffer.numberOfChannels - 1);
          outputChannelData.set(inputChannelData, offset);
        }
      }
      offset += buffer.length;
    }

    return outputBuffer;
  }
}

// ES Module export
export { AudioAnalyzer };
